import { I18n } from "@assets/strings";
import React from "react";
import { Link } from "react-router";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { Path } from "@core/routing/path";
import { useIsLearner, useIsPerson } from "@domain/currentProfile/hooks";
import { usePersonArticles } from "@domain/lms/article/hooks";
import { Person } from "@domain/user/user";
import { fullName } from "@domain/user/utils";
import { ArticleThumbnail } from "@ui/components/article/articleThumbnail";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { Secondary } from "@ui/components/containers/textBlocks";
import { InfiniteScroll } from "@ui/components/infiniteScroll";
import { Stack } from "@ui/components/layout";
import { desktop, gap, mobile } from "@ui/styles/utils";

interface ProfileArticlesListProps extends React.HTMLAttributes<HTMLDivElement> {
	user: Person;
	isTeacher?: boolean;
	organizationName?: string;
}

export const ProfileArticlesList: React.FC<ProfileArticlesListProps> = ({
	user,
	isTeacher,
	organizationName,
	...htmlProps
}) => {
	const isOrganization = user.type === "COMPANY" || user.type === "INSTITUTE";
	const {
		result: articles,
		fetchMore,
		loading,
		totalElements,
		moreLoading,
	} = usePersonArticles(user.id, isOrganization);
	const isAuthor = useIsPerson(user);
	const isLearner = useIsLearner();

	return totalElements === 0 && !loading ? (
		<Stack gap={10}>
			<Secondary>{I18n.t("profile.articles.empty", { user: fullName(user) })}</Secondary>
			{isTeacher && !isLearner && isOrganization && organizationName ? (
				<Link to={Path.ArticleEditor("new")}>
					<PrimaryButton>{I18n.t(`organization.article.incentive`, { institute: organizationName })}</PrimaryButton>
				</Link>
			) : null}
		</Stack>
	) : (
		<InfiniteScroll onEndReached={fetchMore} loading={loading || moreLoading}>
			<Articles {...htmlProps}>
				{articles.map((article) =>
					article ? <ArticleThumbnail key={article.id} article={article} displayStatus={isAuthor} /> : undefined
				)}
				{loading || (moreLoading && <ClipLoader />)}
			</Articles>
		</InfiniteScroll>
	);
};

const Articles = styled.div`
	@media ${desktop} {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		grid-gap: 20px;
	}
	@media ${mobile} {
		${gap(20)};
	}
`;
